import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Container, Paper, Stack, Typography } from '@mui/material';
import { reportFrontendError } from 'API/logging';
import { ZoomLevels } from 'enums';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import { useUserData } from 'hooks/use-user-data';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

const SomethingWentWrong: React.FC<Props> = ({ error, errorInfo }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { token, tenant, id, email, firstName, lastName } = useUserData();

  const [errorNotSent, setErrorNotSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const openSupportEmail = () => {
    // Only include the error message and first few lines of stack trace
    const errorMessage = error?.message || '';
    const shortStack = errorInfo?.componentStack?.split('\n').slice(0, 6).join('\n').trim() || '';
    const body = `Error Message: ${errorMessage}\n\n${shortStack}`;
    window.open(`mailto:info@secret-ai-agent.de?subject=${encodeURIComponent(t('errorInFrontend'))}&body=${encodeURIComponent(body)}`, '_blank');
  };

  useEffect(() => {
    if (error && errorInfo && token && tenant && id && email) {
      const errorPayload = {
        message: error?.message || '',
        stack: errorInfo?.componentStack || '',
      };

      const userDataPayload = {
        tenant,
        id,
        email,
        firstName,
        lastName,
      };

      const path = window.location.href || `${window.location.pathname}${window.location.search}`;

      (async () => {
        setLoading(true);
        const res = await reportFrontendError(path, errorPayload, userDataPayload);

        if (res.error) {
          setErrorNotSent(true);
        }
        setLoading(false);
      })();
    }
  }, [error, errorInfo, token, tenant, id, email]);

  return (
    <Container className={classes.container} maxWidth="md">
      <Paper className={classes.paper} elevation={0}>
        <Stack className={classes.contentStack} gap={5}>
          <Box className={classes.headerBox}>
            <Stack direction="row" alignItems="center" justifyContent="center" gap={2} mb={4}>
              <WarningAmberIcon className={classes.warningIcon} />
              <Typography className={classes.titleText}>{t('somethingWentWrongTitle')}</Typography>
            </Stack>
            <Typography className={classes.descriptionText}>
              {errorNotSent ? t('somethingWentWrongDescriptionErrorNotSent') : t('somethingWentWrongDescriptionErrorSent')}
            </Typography>
          </Box>
          {loading ? (
            <PaiSpinningLoader />
          ) : (
            <Stack className={classes.buttonStack} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <PaiButton text={t('reloadPage')} action={() => window.location.reload()} border={'rounded'} size={errorNotSent ? 'medium' : 'large'} />
              {errorNotSent ? <PaiButton text={t('contactSupport')} action={openSupportEmail} border={'rounded'} /> : null}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    height: 'calc(100vh - 60px - 32px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  paper: {
    borderRadius: '8px',
    padding: '64px 32px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: Shadows.GREY_SHADOW_MAIN,
  },
  contentStack: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  headerBox: {
    flexShrink: 0,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
  },
  descriptionText: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  warningIcon: {
    fontSize: theme.typography.pxToRem(40),
    color: theme.palette.warning.main,
  },
  buttonStack: {
    flexShrink: 0,
  },
}));

export default SomethingWentWrong;
